/**
 * Server function for newsletter subscription via the Shopify Admin API.
 *
 * Uses `customerCreate` with `emailMarketingConsent` for new customers.
 * If the email is already taken, queries the customer and updates their
 * marketing consent via `customerEmailMarketingConsentUpdate`.
 *
 * debt: the Storefront API has a native `customerEmailMarketingSubscribe(email)`
 * (scope `unauthenticated_write_customers`), but on 2026-10-01 only in the
 * `unstable` version, not in any stable one up to 2026-10. Once it reaches a
 * stable version, call it from the client and drop this Admin API function.
 */
import { createServerFn } from "@tanstack/react-start";
import "@shopify/shopify-api/adapters/node";
import { ApiVersion, shopifyApi } from "@shopify/shopify-api";
import { getAdminAccessToken } from "@modules/shopify/adminToken";

// --- Admin GraphQL mutations & queries ---

const CUSTOMER_CREATE_MUTATION = /* GraphQL */ `
  mutation customerCreate($input: CustomerInput!) {
    customerCreate(input: $input) {
      customer {
        id
        email
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const QUERY_CUSTOMER_BY_EMAIL = (email: string) => /* GraphQL */ `
  {
    customers(first: 1, query: "email:${email}") {
      nodes {
        id
        email
        emailMarketingConsent {
          marketingState
        }
      }
    }
  }
`;

const CUSTOMER_EMAIL_MARKETING_CONSENT_UPDATE = /* GraphQL */ `
  mutation customerEmailMarketingConsentUpdate(
    $input: CustomerEmailMarketingConsentUpdateInput!
  ) {
    customerEmailMarketingConsentUpdate(input: $input) {
      customer {
        id
      }
      userErrors {
        field
        message
      }
    }
  }
`;

// --- Types ---

type UserError = { field: Array<string>; message: string };

type CustomerCreateResponse = {
  data?: {
    customerCreate?: {
      customer?: { id: string; email: string };
      userErrors: Array<UserError>;
    };
  };
};

type CustomerQueryResponse = {
  data?: {
    customers: {
      nodes: Array<{
        id: string;
        email: string;
        emailMarketingConsent?: { marketingState: string };
      }>;
    };
  };
};

type ConsentUpdateResponse = {
  data?: {
    customerEmailMarketingConsentUpdate?: {
      customer?: { id: string };
      userErrors: Array<UserError>;
    };
  };
};

function serializeError(error: UserError | undefined): string {
  if (!error) return "UNKNOWN";
  return error.message.toUpperCase().includes("TAKEN")
    ? "EMAIL_TAKEN"
    : error.message || "UNKNOWN";
}

// --- Server function ---

export const subscribeToNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => {
    if (!data.email || typeof data.email !== "string") {
      throw new Error("Email is required");
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      throw new Error("Invalid email format");
    }
    return data;
  })
  .handler(async ({ data }) => {
    const storeDomain = import.meta.env.VITE_SHOPIFY_STORE_URL;
    let adminToken: string;
    try {
      adminToken = await getAdminAccessToken();
    } catch (error) {
      console.error("Newsletter: no Admin API token:", error);
      return { success: false, error: "SERVER_CONFIG_ERROR" };
    }

    const shopify = shopifyApi({
      adminApiAccessToken: adminToken,
      apiKey: adminToken,
      apiSecretKey: adminToken,
      scopes: ["write_customers", "read_customers"],
      hostName: storeDomain,
      apiVersion: ApiVersion.January26,
      isEmbeddedApp: false,
      isCustomStoreApp: true,
    });

    const session = shopify.session.customAppSession(storeDomain);
    const client = new shopify.clients.Graphql({ session });

    // 1. Try creating the customer with marketing consent
    const createResult = (await client.request(CUSTOMER_CREATE_MUTATION, {
      variables: {
        input: {
          email: data.email,
          emailMarketingConsent: {
            consentUpdatedAt: new Date().toISOString(),
            marketingOptInLevel: "SINGLE_OPT_IN",
            marketingState: "SUBSCRIBED",
          },
        },
      },
    })) as CustomerCreateResponse;

    const createErrors = createResult.data?.customerCreate?.userErrors ?? [];

    if (createErrors.length === 0) {
      return { success: true };
    }

    const errorCode = serializeError(createErrors[0]);

    // 2. If email is already taken, update existing customer's consent
    if (errorCode === "EMAIL_TAKEN") {
      const queryResult = (await client.request(
        QUERY_CUSTOMER_BY_EMAIL(data.email),
      )) as CustomerQueryResponse;

      const customers = queryResult.data?.customers.nodes ?? [];
      if (customers.length === 0) {
        return { success: true }; // Privacy: don't reveal lookup failure
      }

      const customer = customers[0];

      if (customer.emailMarketingConsent.marketingState === "SUBSCRIBED") {
        return { success: true, alreadySubscribed: true };
      }

      const updateResult = (await client.request(
        CUSTOMER_EMAIL_MARKETING_CONSENT_UPDATE,
        {
          variables: {
            input: {
              customerId: customer.id,
              emailMarketingConsent: {
                consentUpdatedAt: new Date().toISOString(),
                marketingOptInLevel: "SINGLE_OPT_IN",
                marketingState: "SUBSCRIBED",
              },
            },
          },
        },
      )) as ConsentUpdateResponse;

      const updateErrors =
        updateResult.data?.customerEmailMarketingConsentUpdate?.userErrors ??
        [];

      if (updateErrors.length > 0) {
        console.error("Newsletter consent update failed:", updateErrors);
        return { success: false, error: serializeError(updateErrors[0]) };
      }

      return { success: true };
    }

    // 3. Unexpected error
    console.error("Newsletter subscription failed:", createErrors);
    return { success: false, error: errorCode };
  });
